import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

import { RequireActingAsEntityDialog } from '../components/require-acting-as-entity-dialog';
import { useActingAsEntity } from './useActingAsEntity';

type RequireFn = (callback: () => void) => void;

const RequireActingAsContext = createContext<RequireFn>(() => {
  /* no-op default — overridden by RequireActingAsEntityProvider */
});

export const RequireActingAsEntityProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { actingAsEntity, isStaff } = useActingAsEntity();
  const [open, setOpen] = useState(false);
  const queuedRef = useRef<(() => void) | null>(null);

  const require = useCallback<RequireFn>(
    (callback) => {
      if (!isStaff || actingAsEntity) {
        callback();
        return;
      }
      queuedRef.current = callback;
      setOpen(true);
    },
    [isStaff, actingAsEntity]
  );

  const handleClose = () => {
    queuedRef.current = null;
    setOpen(false);
  };

  const handleSelected = () => {
    const cb = queuedRef.current;
    queuedRef.current = null;
    cb?.();
  };

  return (
    <RequireActingAsContext.Provider value={require}>
      {children}
      <RequireActingAsEntityDialog open={open} onClose={handleClose} onSelected={handleSelected} />
    </RequireActingAsContext.Provider>
  );
};

export const useRequireActingAsEntity = () => useContext(RequireActingAsContext);
